/**
 * Hook for managing user-friendly error messages
 * Maps technical errors to user-friendly messages
 */

import { useCallback } from 'react';
import { ConversationState } from '../types/conversation';
import { DEFAULT_ERROR_MESSAGES } from '../config/errorMessages';

type ConnectionStatus = ConversationState['connectionStatus'];

export interface UseErrorMessagesReturn {
  getConnectionMessage: (status: ConnectionStatus, error?: string | null) => string;
  getRetryMessage: (attemptCount: number) => string;
  isUserFriendlyError: (error: string) => boolean;
  getFriendlyError: (error: string) => string;
}

export const useErrorMessages = (): UseErrorMessagesReturn => {
  const isUserFriendlyError = useCallback((error: string): boolean => {
    const technicalErrors = [
      'Failed to fetch',
      'NetworkError',
      'TypeError',
      'ReferenceError',
      'WebSocket connection',
      'ECONNREFUSED',
      'ETIMEDOUT',
    ];
    
    return !technicalErrors.some(techError => error.includes(techError));
  }, []);

  const getFriendlyError = useCallback((error: string): string => {
    const messages = DEFAULT_ERROR_MESSAGES;
    
    // Map common technical errors to friendly messages
    if (error.includes('Failed to fetch')) {
      return messages.user.networkIssue;
    }
    
    if (error.includes('NetworkError') || error.includes('net::')) {
      return messages.user.networkIssue;
    }
    
    if (error.includes('timeout') || error.includes('TIMEOUT')) {
      return messages.connection.timeout;
    }
    
    if (error.includes('WebSocket') || error.includes('connection')) {
      return messages.connection.failed;
    }
    
    if (error.includes('configuration') || error.includes('config')) {
      return messages.backend.configFailed;
    }
    
    if (error.includes('server') || error.includes('Service')) {
      return messages.backend.serviceError;
    }
    
    // If it's already user-friendly, return as-is
    if (isUserFriendlyError(error)) {
      return error;
    }
    
    // Default fallback for unknown technical errors
    return messages.backend.unavailable;
  }, [isUserFriendlyError]);

  const getConnectionMessage = useCallback((status: ConnectionStatus, error?: string | null): string => {
    const messages = DEFAULT_ERROR_MESSAGES;
    
    switch (status) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return messages.connection.retrying;
      case 'disconnected':
        if (error) {
          // Check for specific error patterns and return friendly messages
          if (error.includes('Failed to fetch') || error.includes('NetworkError')) {
            return messages.user.networkIssue;
          }
          if (error.includes('timeout') || error.includes('TIMEOUT')) {
            return messages.connection.timeout;
          }
          if (error.includes('configuration') || error.includes('config')) {
            return messages.backend.configFailed;
          }
          // Return friendly version of the error
          return getFriendlyError(error);
        }
        return messages.connection.offline;
      default:
        return messages.connection.offline;
    }
  }, [getFriendlyError]);

  const getRetryMessage = useCallback((attemptCount: number): string => {
    const messages = DEFAULT_ERROR_MESSAGES;
    
    if (attemptCount === 0) {
      return messages.user.retryPrompt;
    }
    
    if (attemptCount < 3) {
      return `Retrying connection... (attempt ${attemptCount})`;
    }
    
    return `Connection issues persist. ${messages.user.retryPrompt}`;
  }, []);

  return {
    getConnectionMessage,
    getRetryMessage,
    isUserFriendlyError,
    getFriendlyError,
  };
};
